/*
  Renoura cart drawer behaviour (17 Oct 2026). Four custom elements, no dependencies besides Dawn's cart-drawer.js:
  - renoura-cart-timer   "Your order is reserved for mm:ss". Deadline kept in localStorage, never restarted while the cart has items.
  - renoura-cart-upsell  add-to-cart buttons on the upsell cards (section rendering API, drawer re-renders in place).
  - renoura-cart-gift    adds / removes the free gift line when the cart (de)qualifies.
  - renoura-cart-popup   offer before checkout, once per visit.
  All state is re-read from the server-rendered markup after every drawer re-render.
*/
(() => {
  const SECTIONS = 'cart-drawer,cart-icon-bubble';
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const routeUrl = (name, fallback) => (window.routes && window.routes[name]) || root() + fallback;

  const safeGet = (store, key) => {
    try {
      return window[store + 'Storage'].getItem(key);
    } catch (e) {
      return null;
    }
  };
  const safeSet = (store, key, value) => {
    try {
      window[store + 'Storage'].setItem(key, value);
    } catch (e) {
      /* storage blocked: timer just runs from page load */
    }
  };
  const safeRemove = (store, key) => {
    try {
      window[store + 'Storage'].removeItem(key);
    } catch (e) {
      /* ignore */
    }
  };

  const drawerEl = () => document.querySelector('cart-drawer');

  const renderDrawer = (json, itemCount) => {
    const drawer = drawerEl();
    if (!drawer || !json || !json.sections) return;
    drawer.classList.toggle('is-empty', itemCount === 0);
    drawer.renderContents({ id: json.id, sections: json.sections });
  };

  const postJson = (url, body) =>
    fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

  const addVariant = async (variantId, quantity = 1) => {
    const res = await postJson(routeUrl('cart_add_url', 'cart/add') + '.js', {
      items: [{ id: Number(variantId), quantity }],
      sections: SECTIONS,
      sections_url: window.location.pathname,
    });
    const json = await res.json();
    if (!res.ok || json.status) throw new Error(json.description || json.message || 'add failed');
    const cart = await fetch(root() + 'cart.js', { credentials: 'same-origin' }).then((r) => r.json());
    return { json, itemCount: cart.item_count };
  };

  const pad = (n) => String(n).padStart(2, '0');
  const fmt = (ms) => {
    const total = Math.max(0, Math.ceil(ms / 1000));
    return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
  };

  /* ---------------------------------------------------------- timer */
  class RenouraCartTimer extends HTMLElement {
    connectedCallback() {
      this.key = 'renoura_timer_' + (this.dataset.key || 'cart');
      this.store = this.dataset.store === 'session' ? 'session' : 'local';
      this.ms = Math.max(1, Number(this.dataset.minutes) || 10) * 60 * 1000;
      this.out = this.querySelector('[data-rc-timer-text]');
      const items = Number(this.dataset.items) || 0;

      if (items === 0) {
        // Empty cart: forget the old deadline, so the next cart gets a fresh timer (never restarted while it has items).
        safeRemove(this.store, this.key);
        return;
      }
      if (this.hasAttribute('data-manual')) {
        // Started by the pop-up when it opens
        if (this.deadline()) this.tick();
        return;
      }
      this.start();
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    deadline() {
      const raw = Number(safeGet(this.store, this.key));
      return raw > 0 ? raw : 0;
    }

    start() {
      if (!this.deadline()) safeSet(this.store, this.key, String(Date.now() + this.ms));
      this.tick();
    }

    tick() {
      clearInterval(this.timer);
      const render = () => {
        const left = this.deadline() - Date.now();
        if (!this.out) return false;
        if (left <= 0) {
          this.out.textContent = this.dataset.ended || '';
          this.classList.add('is-ended');
          return false;
        }
        const text = (this.dataset.text || '[time]').replace('[time]', fmt(left));
        this.out.textContent = text;
        return true;
      };
      if (render()) {
        this.timer = setInterval(() => {
          if (!render()) clearInterval(this.timer);
        }, 1000);
      }
    }
  }

  /* ---------------------------------------------------------- upsell card */
  class RenouraCartUpsell extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-rc-add]');
      this.select = this.querySelector('[data-rc-select]');
      this.price = this.querySelector('[data-rc-price]');
      this.was = this.querySelector('[data-rc-was]');
      this.error = this.querySelector('[data-rc-error]');
      if (this.button) this.button.addEventListener('click', () => this.add());
      if (this.select) this.select.addEventListener('change', () => this.onVariant());
    }

    onVariant() {
      const opt = this.select.selectedOptions[0];
      if (!opt) return;
      if (this.price) this.price.textContent = opt.dataset.price || '';
      if (this.was) {
        this.was.textContent = opt.dataset.was || '';
        this.was.hidden = !opt.dataset.was;
      }
    }

    async add() {
      const id = this.select ? this.select.value : this.button.dataset.variantId;
      if (!id || id === '0') return;
      const label = this.querySelector('[data-rc-add-label]');
      const original = label ? label.textContent : '';
      this.button.disabled = true;
      if (label && this.dataset.adding) label.textContent = this.dataset.adding;
      if (this.error) this.error.hidden = true;
      try {
        const { json, itemCount } = await addVariant(id);
        renderDrawer(json, itemCount);
      } catch (e) {
        if (this.error) this.error.hidden = false;
        this.button.disabled = false;
        if (label) label.textContent = original;
      }
    }
  }

  /* ---------------------------------------------------------- gift */
  let giftFailed = '';
  let giftBusy = false;

  class RenouraCartGift extends HTMLElement {
    connectedCallback() {
      setTimeout(() => this.sync(), 0);
    }

    async sync() {
      if (giftBusy) return;
      const variant = this.dataset.variant;
      const key = this.dataset.key;
      const qualifies = this.dataset.qualifies === 'true';
      if (!variant) return;
      try {
        if (qualifies && !key) {
          if (giftFailed === variant) return;
          giftBusy = true;
          const { json, itemCount } = await addVariant(variant, 1);
          renderDrawer(json, itemCount);
        } else if (!qualifies && key) {
          giftBusy = true;
          const res = await postJson(routeUrl('cart_change_url', 'cart/change') + '.js', {
            id: key,
            quantity: 0,
            sections: SECTIONS,
            sections_url: window.location.pathname,
          });
          const json = await res.json();
          if (!res.ok || json.status) throw new Error('remove failed');
          renderDrawer(json, json.item_count);
        }
      } catch (e) {
        giftFailed = variant;
      } finally {
        giftBusy = false;
      }
    }
  }

  /* ---------------------------------------------------------- insured shipping (Eve Curls) */
  // Checkbox card above the checkout button. The insurance is a normal product in the cart (counts for the
  // free shipping bar). Pre-ticked: added once the cart has paying items, until the customer unticks it
  // (remembered for the session). With no paying items left the insurance line is removed again.
  const INS_OFF = 'renoura_insurance_off';
  let insBusy = false;

  class RenouraCartInsurance extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.box = this.querySelector('[data-rc-ins-box]');
      if (this.box) this.box.addEventListener('change', () => this.toggle());
      setTimeout(() => this.sync(), 0);
    }

    async sync() {
      if (insBusy) return;
      const variant = this.dataset.variant;
      const key = this.dataset.key;
      const inCart = this.dataset.inCart === 'true';
      const paying = Number(this.dataset.paying || 0);
      const off = safeGet('session', INS_OFF) === '1';
      try {
        if (paying === 0 && inCart && key) {
          insBusy = true;
          await this.remove(key);
        } else if (paying > 0 && !inCart && this.dataset.preticked === 'true' && !off) {
          insBusy = true;
          const { json, itemCount } = await addVariant(variant, 1);
          renderDrawer(json, itemCount);
        }
      } catch (e) {
        /* leave the cart as it is */
      } finally {
        insBusy = false;
      }
    }

    async remove(key) {
      const res = await postJson(routeUrl('cart_change_url', 'cart/change') + '.js', {
        id: key,
        quantity: 0,
        sections: SECTIONS,
        sections_url: window.location.pathname,
      });
      const json = await res.json();
      if (!res.ok || json.status) throw new Error('remove failed');
      renderDrawer(json, json.item_count);
    }

    async toggle() {
      if (insBusy || !this.box) return;
      insBusy = true;
      this.box.disabled = true;
      try {
        if (this.box.checked) {
          safeRemove('session', INS_OFF);
          const { json, itemCount } = await addVariant(this.dataset.variant, 1);
          renderDrawer(json, itemCount);
        } else {
          safeSet('session', INS_OFF, '1');
          if (this.dataset.key) await this.remove(this.dataset.key);
        }
      } catch (e) {
        this.box.checked = !this.box.checked;
        this.box.disabled = false;
      } finally {
        insBusy = false;
      }
    }
  }

  /* ---------------------------------------------------------- pre-checkout pop-up */
  class RenouraCartPopup extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.seenKey = 'renoura_popup_seen';
      this.variant = this.dataset.variant;
      this.productId = Number(this.dataset.productId);
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-rc-pop-close]')) this.close();
        if (e.target.closest('[data-rc-pop-accept]')) this.accept();
        if (e.target.closest('[data-rc-pop-skip]')) this.skip();
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          this.close();
        }
      });
      // Capture phase: runs before the form submits. Other checkout buttons are not touched.
      document.addEventListener(
        'click',
        (e) => {
          const btn = e.target.closest('#CartDrawer-Checkout');
          if (!btn || safeGet('session', this.seenKey)) return;
          e.preventDefault();
          e.stopPropagation();
          this.maybeOpen(btn);
        },
        true
      );
    }

    async maybeOpen(btn) {
      this.checkoutBtn = btn;
      let inCart = false;
      try {
        const cart = await fetch(root() + 'cart.js', { credentials: 'same-origin' }).then((r) => r.json());
        inCart = cart.items.some((i) => i.product_id === this.productId);
      } catch (e) {
        inCart = true; // cannot check: do not block checkout
      }
      if (inCart) return this.goCheckout();
      this.open();
    }

    open() {
      this.hidden = false;
      const timer = this.querySelector('renoura-cart-timer');
      if (timer && timer.start) timer.start();
      const cta = this.querySelector('[data-rc-pop-accept]');
      if (cta) cta.focus();
    }

    close() {
      this.hidden = true;
      if (this.checkoutBtn) this.checkoutBtn.focus();
    }

    skip() {
      safeSet('session', this.seenKey, '1');
      this.goCheckout();
    }

    async accept() {
      const err = this.querySelector('[data-rc-pop-error]');
      const cta = this.querySelector('[data-rc-pop-accept]');
      if (err) err.hidden = true;
      if (cta) cta.disabled = true;
      try {
        await addVariant(this.variant, 1);
        safeSet('session', this.seenKey, '1');
        this.goCheckout();
      } catch (e) {
        if (err) err.hidden = false;
        if (cta) cta.disabled = false;
      }
    }

    goCheckout() {
      this.hidden = true;
      const form = document.getElementById('CartDrawer-Form');
      const btn = document.getElementById('CartDrawer-Checkout') || this.checkoutBtn;
      try {
        if (form && btn && form.requestSubmit) return form.requestSubmit(btn);
      } catch (e) {
        /* fall through to the direct link */
      }
      window.location.href = root() + 'checkout';
    }
  }

  const define = (name, cls) => {
    if (!customElements.get(name)) customElements.define(name, cls);
  };
  define('renoura-cart-timer', RenouraCartTimer);
  define('renoura-cart-upsell', RenouraCartUpsell);
  define('renoura-cart-gift', RenouraCartGift);
  define('renoura-cart-insurance', RenouraCartInsurance);
  define('renoura-cart-popup', RenouraCartPopup);
})();
